import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useChat } from "@/chat/store"

/**
 * Connection and error surface.
 *
 * The socket drops routinely — server restarts, sleeping phones, flaky
 * networks — and reconnection is automatic, so this stays quiet while things
 * are working and explains itself only when they are not.
 */
export function ConnectionBanner() {
  const { status, protocolMismatch, notice, retry, dismissNotice } = useChat()

  if (protocolMismatch) {
    return (
      <Banner
        actions={
          <Button size="sm" variant="default" onClick={() => location.reload()}>
            Перезагрузить
          </Button>
        }
      >
        <AlertTitle>Приложение устарело по сравнению с сервером</AlertTitle>
        <AlertDescription>
          Перезагрузите страницу, чтобы получить актуальную версию.
        </AlertDescription>
      </Banner>
    )
  }

  if (status === "offline") {
    return (
      <Banner
        actions={
          <Button size="sm" variant="outline" onClick={retry}>
            Повторить
          </Button>
        }
      >
        <AlertTitle>Переподключение…</AlertTitle>
      </Banner>
    )
  }

  if (notice) {
    return (
      <Banner
        actions={
          <Button size="sm" variant="ghost" onClick={dismissNotice}>
            Закрыть
          </Button>
        }
      >
        <AlertTitle>{notice}</AlertTitle>
      </Banner>
    )
  }

  if (status === "connecting") {
    return (
      <div className="flex flex-none items-center justify-center gap-2 border-b py-1.5 text-xs text-muted-foreground">
        <Spinner className="size-3" />
        Подключение…
      </div>
    )
  }

  return null
}

/**
 * One banner row: the message, and its button beside it.
 *
 * Not `AlertAction`: that slot is absolutely positioned over a fixed strip
 * of padding, which every label here is wider than — on a phone the button
 * sat on top of the text. Laying the two out as a wrapping row lets the
 * button drop below the message when the screen is too narrow for both;
 * the floor on the message column is what triggers that wrap rather than
 * letting the text squeeze into a ribbon beside the button.
 */
function Banner({
  children,
  actions,
}: {
  children: React.ReactNode
  actions: React.ReactNode
}) {
  return (
    <div className="flex-none border-b p-2">
      <Alert className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="min-w-48 flex-1">{children}</div>
        <div className="flex flex-none gap-1.5">{actions}</div>
      </Alert>
    </div>
  )
}
